import { SearchRepository } from './search.repository.js'
import type { SearchQuery, SearchResponse, DiscoveryFeed } from '@my-roadtrip/types'
import { redisClient } from '../../core/redis/client.js'
import { createLogger } from '../../common/utils/logger.js'
import { safeJsonParse } from '../../utils/sanitize.js'
import { POIDatabaseService } from '../ai/poi-database.service.js'
import { db } from '../../core/firebase/admin.js'

const logger = createLogger('SearchService')

export class SearchService {
  private readonly repository = new SearchRepository()
  private readonly poiDatabase = new POIDatabaseService(db)
  private readonly SEARCH_CACHE_TTL = 300 // 5 minutes
  private readonly FEED_CACHE_TTL = 600 // 10 minutes

  async search(query: SearchQuery): Promise<SearchResponse> {
    logger.debug('Searching', { query: query.q, filters: Object.keys(query.filters || {}).length })
    const cacheKey = this.buildSearchCacheKey(query)

    try {
      const cached = await redisClient.get(cacheKey)
      if (cached) {
        logger.debug('Search cache hit', { query: query.q })
        const parsed = safeJsonParse<SearchResponse>(cached)
        if (parsed) return parsed
      }
    } catch {
      // Cache miss, continue to repository
      logger.debug('Search cache miss or error', { query: query.q })
    }

    logger.info('Executing search query', { query: query.q })
    const response = await this.repository.search(query)
    logger.info('Search completed', { query: query.q, resultsCount: response.results.length })

    try {
      await redisClient.setex(cacheKey, this.SEARCH_CACHE_TTL, JSON.stringify(response))
      logger.debug('Search result cached', { query: query.q, ttl: this.SEARCH_CACHE_TTL })
    } catch {
      // Cache write failure, non-critical
      logger.warn('Failed to cache search result', { query: query.q })
    }

    return response
  }

  async searchPOIs(query: string): Promise<any[]> {
    logger.debug('Searching POIs', { query })
    // Basic cache for POI search
    const cacheKey = `search:pois:${query}`

    try {
      const cached = await redisClient.get(cacheKey)
      if (cached) {
        return JSON.parse(cached)
      }
    } catch {
      // Ignore cache error
    }

    const results = await this.poiDatabase.searchByName(query)

    try {
      if (results.length > 0) {
        await redisClient.setex(cacheKey, 300, JSON.stringify(results))
      }
    } catch {
      // Ignore cache error
    }

    return results
  }

  async getDiscoveryFeed(): Promise<DiscoveryFeed> {
    logger.debug('Fetching discovery feed')
    const cacheKey = 'search:feed'

    try {
      const cached = await redisClient.get(cacheKey)
      if (cached) {
        logger.debug('Discovery feed cache hit')
        const parsed = safeJsonParse<DiscoveryFeed>(cached)
        if (parsed) return parsed
      }
    } catch {
      // Cache miss, continue to repository
      logger.debug('Discovery feed cache miss or error')
    }

    logger.info('Fetching discovery feed from repository')
    const feed = await this.repository.getDiscoveryFeed()
    logger.info('Discovery feed fetched', {
      trendingCount: feed.trending.length,
      recentCount: feed.recent.length,
      popularCount: feed.popular.length
    })

    try {
      await redisClient.setex(cacheKey, this.FEED_CACHE_TTL, JSON.stringify(feed))
      logger.debug('Discovery feed cached', { ttl: this.FEED_CACHE_TTL })
    } catch {
      // Cache write failure, non-critical
      logger.warn('Failed to cache discovery feed')
    }

    return feed
  }

  async indexPlan(planId: string): Promise<void> {
    logger.info('Indexing plan', { planId })
    await this.repository.indexPlan(planId)
    await this.invalidateCache()
    logger.info('Plan indexed and cache invalidated', { planId })
  }

  async removePlan(planId: string): Promise<void> {
    await this.repository.removePlan(planId)
    await this.invalidateCache()
  }

  private buildSearchCacheKey(query: SearchQuery): string {
    const parts = [
      'search',
      query.q,
      query.page?.toString() || '1',
      query.limit?.toString() || '20'
    ]

    if (query.filters?.countries?.length) {
      parts.push(`countries:${query.filters.countries.join(',')}`)
    }
    if (query.filters?.tags?.length) {
      parts.push(`tags:${query.filters.tags.join(',')}`)
    }
    if (query.filters?.minDistance) {
      parts.push(`minDist:${query.filters.minDistance}`)
    }
    if (query.filters?.maxDistance) {
      parts.push(`maxDist:${query.filters.maxDistance}`)
    }
    if (query.filters?.minDuration) {
      parts.push(`minDur:${query.filters.minDuration}`)
    }
    if (query.filters?.maxDuration) {
      parts.push(`maxDur:${query.filters.maxDuration}`)
    }

    return parts.join(':')
  }

  private async invalidateCache(): Promise<void> {
    try {
      const keys = await redisClient.keys('search:*')
      if (keys.length > 0) {
        await redisClient.del(...keys)
      }
    } catch {
      // Cache invalidation failure, non-critical
    }
  }
}
